import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useSettings } from "@/context/SettingsContext";

const CartContext = createContext(null);
const STORAGE_KEY = "mn_cart_v1";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function CartProvider({ children }) {
  const settings = useSettings();
  const [items, setItems] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
      return Array.isArray(saved) ? saved : [];
    } catch (e) {
      return [];
    }
  });
  const [toast, setToast] = useState("");
  const [promo, setPromo] = useState(null); // { code, discount }

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const showToast = useCallback((msg) => {
    setToast(msg);
    window.clearTimeout(window.__mnToast);
    window.__mnToast = window.setTimeout(() => setToast(""), 3000);
  }, []);

  const addItem = useCallback((product, qty = 1) => {
    const price = product.effective_price != null ? product.effective_price : product.price;
    setItems((prev) => {
      const existing = prev.find((i) => i.product_id === product.id);
      if (existing) {
        return prev.map((i) => (i.product_id === product.id ? { ...i, quantity: i.quantity + qty, price } : i));
      }
      return [...prev, { product_id: product.id, name: product.name, subtitle: product.subtitle, price, image: product.image, quantity: qty }];
    });
    showToast(`✓ ${product.name} ajouté au panier`);
  }, [showToast]);

  const updateQty = useCallback((productId, qty) => {
    setItems((prev) => prev.map((i) => (i.product_id === productId ? { ...i, quantity: Math.max(0, qty) } : i)).filter((i) => i.quantity > 0));
  }, []);

  const removeItem = useCallback((productId) => {
    setItems((prev) => prev.filter((i) => i.product_id !== productId));
  }, []);

  const clearCart = useCallback(() => { setItems([]); setPromo(null); }, []);

  const subtotal = Math.round(items.reduce((s, i) => s + i.price * i.quantity, 0) * 100) / 100;

  const applyPromo = useCallback(async (code) => {
    const res = await axios.post(`${API}/promo/validate`, { code, subtotal });
    setPromo({ code: res.data.code, discount: res.data.discount, type: res.data.type, value: res.data.value });
    return res.data;
  }, [subtotal]);

  const clearPromo = useCallback(() => setPromo(null), []);

  const threshold = Number(settings.free_shipping_threshold ?? 45);
  const fee = Number(settings.shipping_fee ?? 4.9);
  const discount = promo ? Math.min(promo.discount, subtotal) : 0;
  const discountedSubtotal = Math.round((subtotal - discount) * 100) / 100;
  const shipping = subtotal === 0 ? 0 : discountedSubtotal >= threshold ? 0 : fee;
  const total = Math.round((discountedSubtotal + shipping) * 100) / 100;
  const count = items.reduce((s, i) => s + i.quantity, 0);

  return (
    <CartContext.Provider
      value={{
        items, count, subtotal, discount, shipping, total, promo,
        addItem, updateQty, removeItem, clearCart, showToast,
        applyPromo, clearPromo, FREE_SHIPPING_THRESHOLD: threshold,
      }}
    >
      {children}
      <div id="toast" className={toast ? "show" : ""} role="status" aria-live="polite" data-testid="cart-toast">{toast}</div>
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
